import { Box, Stack, Typography, Link } from "@mui/material";
import Calendar from "assets/icons/Calendar";
import PdfFile from "assets/icons/file-icons/PdfFile";
import DocFile from "assets/icons/file-icons/DocFile";
import Mp4File from "assets/icons/file-icons/Mp4File";
import OtherFile from "assets/icons/file-icons/OtherFile";
import PngFile from "assets/icons/file-icons/PngFile";
import PptFile from "assets/icons/file-icons/PptFile";
import SvgFile from "assets/icons/file-icons/SvgFile";
import TxtFile from "assets/icons/file-icons/TxtFile";
import XlsFile from "assets/icons/file-icons/XlsFile";
import ZipFile from "assets/icons/file-icons/ZipFile";
import Download from "assets/icons/Download";
import { FC } from "react";

export interface DocumentCardProps {
	doc: {
		title: string;
		publishedAt: string;
		type : {
			title: string;
			color: string;
		} | null;
		audiences: string[]
		file: {
			url: string;
			name: string;
		}
	}
}

const PdfFileComponent: FC<{extension: string | null}> = (props) => <PdfFile />
const DocFileComponent: FC<{extension: string | null}> = (props) => <DocFile />
const Mp4FileComponent: FC<{extension: string | null}> = (props) => <Mp4File />
const PngFileComponent: FC<{extension: string | null}> = (props) => <PngFile />
const PptFileComponent: FC<{extension: string | null}> = (props) => <PptFile />
const SvgFileComponent: FC<{extension: string | null}> = (props) => <SvgFile />
const TxtFileComponent: FC<{extension: string | null}> = (props) => <TxtFile />
const XlsFileComponent: FC<{extension: string | null}> = (props) => <XlsFile />
const ZipFileComponent: FC<{extension: string | null}> = (props) => <ZipFile />
const OtherFileComponent: FC<{ extension: string }> = ({ extension }) => (
	<OtherFile extension={extension} />
);

const fileExtMap: Record<string, FC<{extension: string | null}>> = {
	'.pdf': PdfFileComponent,
	'.mp4': Mp4FileComponent,
	'.doc': DocFileComponent,
	'.docx': DocFileComponent,
	'.png': PngFileComponent,
	'.ppt': PptFileComponent,
	'.pptx': PptFileComponent,
	'.svg': SvgFileComponent,
	'.txt': TxtFileComponent,
	'.xls': XlsFileComponent,
	'.xlsx': XlsFileComponent,
	'.zip': ZipFileComponent,
}

const getDocumentComponent = (extension: string): FC<any> => {
	return fileExtMap[extension] || OtherFileComponent;
};

const getFileExtension = (fileName: string): string => {
	const lastDot = fileName.lastIndexOf('.');

	if (lastDot === -1 || lastDot === 0) {
		return '';
	}

	return fileName.substring(lastDot).toLowerCase();
};

const DocumentCard = ({ doc }: DocumentCardProps) => {
	const date = new Date(doc.publishedAt).toLocaleDateString('ru-RU', {
		day: 'numeric',
		month: 'long',
		year: 'numeric'
	});

	const mainColor = "#252525";

	const FileExtIcon = getDocumentComponent(getFileExtension(doc.file.name));

	return (
		<Box sx={{
			p: 3,
			bgcolor: '#FFFFFF',
			borderRadius: 4,
			border: '1px solid',
			borderColor: '#F2F2F2',
			width: '100%',
			boxSizing: 'border-box'
		}}>
			<Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">

				<Stack spacing={2} sx={{ flexGrow: 1 }}>

					<Box sx={{ display: 'flex' }}>
						<Stack
							direction="row"
							alignItems="center"
							spacing={1}
							sx={{
								px: 1.5,
								py: 0.5,
								border: '1px solid #EEEEEE',
								borderRadius: 2
							}}
						>
							{doc.type && (
								<>
									<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: doc.type.color }} />
									<Typography variant="caption" sx={{ color: mainColor, fontWeight: 600 }}>
										{doc.type.title}
									</Typography>
								</>
							)}
						</Stack>
					</Box>

					<Typography
						variant="h6"
						sx={{
							color: mainColor,
							fontWeight: 700,
							lineHeight: 1.3,
							fontSize: '1.125rem'
						}}
					>
						{doc.title}
					</Typography>

					<Stack direction="row" spacing={1} flexWrap="wrap">
						{doc.audiences.map(tag => (
							<Box key={tag} sx={{ px: 1.5, py: 0.5, bgcolor: '#F9F9F9', borderRadius: 1.5 }}>
								<Typography variant="caption" sx={{ color: '#888888', fontWeight: 500 }}>
									{tag}
								</Typography>
							</Box>
						))}
					</Stack>

					<Stack spacing={1.5} sx={{ pt: 1 }}>

						<Stack direction="row" alignItems="center" spacing={1} sx={{ color: '#999999' }}>
							<Calendar sx={{ width: 20, height: 20 }} />
							<Typography variant="body2" sx={{ fontSize: '14px', color: '#999999' }}>
								{date}
							</Typography>
						</Stack>

						<Link
							href={doc.file.url}
							target="_blank"
							download
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1.5,
								color: '#E10B17',
								textDecoration: 'none',
								width: 'fit-content',
								'&:hover': { opacity: 0.8 }
							}}
						>
							<Download sx={{ width: 32, height: 32 }} />
							<Typography variant="body1" sx={{ fontWeight: 700, fontSize: '16px' }}>
								Скачать
							</Typography>
						</Link>
					</Stack>
				</Stack>

				<Box sx={{
					flexShrink: 0,
					ml: 2,
					'& svg': {
						width: '47px',
						height: '56px',
						display: 'block'
					}
				}}>
					<FileExtIcon />
				</Box>

			</Stack>
		</Box>
	);
};

export default DocumentCard;